import axiosInstance from "../config/axiosConfig";
import { updateProjectByName } from "../store/slices/projectsSlice";

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

/**
 * Map ops/bucket names that were wrongly stored on lead.Status back to
 * Add/Edit Lead dropdown values (Won / Pending / Lost / Won Recurring).
 */
export const toLeadDropdownStatus = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === "project approved") return "Won";
  if (
    normalized === "response received" ||
    normalized === "project requested"
  ) {
    return "Pending";
  }
  if (!status) return status;
  // Preserve known dropdown casing when possible
  if (normalized === "won") return "Won";
  if (normalized === "pending") return "Pending";
  if (normalized === "lost") return "Lost";
  if (normalized === "won recurring") return "Won Recurring";
  return status;
};

export const isWonLeadStatus = (status) => {
  const normalized = normalizeStatus(status);
  // "project approved" = Status field wrongly overwritten with bucket name
  return (
    normalized === "won" ||
    normalized === "won recurring" ||
    normalized === "project approved"
  );
};

/** True when lead Status is Lost (hide from My Requests baskets). */
export const isLostLeadStatus = (status) =>
  normalizeStatus(status) === "lost";

/** Match a ManagerMainProject row to its lead (quotationId preferred). */
export const findMatchingLeadForProject = (project, leads = []) => {
  if (!project || !Array.isArray(leads) || leads.length === 0) return null;
  return (
    leads.find((lead) => {
      if (project.quotationId != null && lead.quotationId != null) {
        return String(lead.quotationId) === String(project.quotationId);
      }
      return (
        String(lead.ProjectName || "").trim() ===
        String(project.ProjectName || "").trim()
      );
    }) || null
  );
};

/** True when the project belongs to a Lost lead — exclude from My Requests. */
export const isProjectLinkedToLostLead = (project, leads = []) => {
  const lead = findMatchingLeadForProject(project, leads);
  return lead ? isLostLeadStatus(lead.Status) : false;
};

const buildLeadKey = (obj = {}) =>
  `${obj.ProjectName || ""}||${obj.ClientName || ""}||${obj.AssignedGroup || ""}`
    .toLowerCase()
    .trim();

const FORWARDED_SALESPERSON_STATUSES = [
  "project assigned",
  "project ongoing",
  "project complete",
];

const isForwardedAssignee = (person) => {
  const spStatus = normalizeStatus(person?.salespersonstatus);
  return FORWARDED_SALESPERSON_STATUSES.includes(spStatus);
};

/** True only after SP forwards from Project Approved (assignee moved past project approved). */
export const hasManagerForwardedFromProjectApproved = (
  projects = [],
  quotationId
) => {
  const qid = String(quotationId ?? "").trim();
  if (!qid || !Array.isArray(projects) || projects.length === 0) return false;

  return projects
    .filter((proj) => String(proj?.quotationId ?? "").trim() === qid)
    .some((proj) =>
      (proj.Assignedperson || []).some((person) =>
        isForwardedAssignee(person)
      )
    );
};

const isOutstanding = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const collectLeadProjects = (projects, quotationId, leadMeta) => {
  const qid = String(quotationId ?? "").trim();
  if (qid) {
    const byQuotation = (projects || []).filter(
      (proj) => String(proj?.quotationId ?? "").trim() === qid
    );
    if (byQuotation.length) return byQuotation;
  }

  const leadKey = buildLeadKey(leadMeta);
  if (!leadKey) return [];

  return (projects || []).filter((proj) => buildLeadKey(proj) === leadKey);
};

const resolveManagerEmail = (project) => {
  const value = project?.AssignedManagerEmail;
  if (Array.isArray(value)) return value[0] || "";
  return value || "";
};

const mapAssigneeForSync = (person, isNowWon) => {
  if (isForwardedAssignee(person)) return person;

  const spStatus = normalizeStatus(person?.salespersonstatus);
  const spAction = normalizeStatus(person?.salespersonaction);

  if (isNowWon) {
    if (spStatus === "project approved") return person;
    if (spStatus === "response received") {
      return { ...person, salespersonstatus: "project approved" };
    }
    return person;
  }

  // Won → Pending: move non-forwarded assignees back into Response Received.
  // RR bucket requires salespersonstatus === "response received" AND
  // isProjectoutstanding === false (strict), so always set both.
  const hadAcceptedResponse =
    spStatus === "project approved" ||
    spStatus === "response received" ||
    spAction === "accept";

  return {
    ...person,
    salespersonstatus: "response received",
    isProjectoutstanding: false,
    ...(hadAcceptedResponse ? { salespersonaction: "accept" } : {}),
  };
};

const assigneesChanged = (before = [], after = []) => {
  if (before.length !== after.length) return true;
  return after.some((person, index) => {
    const prev = before[index] || {};
    return (
      normalizeStatus(person?.salespersonstatus) !==
        normalizeStatus(prev?.salespersonstatus) ||
      normalizeStatus(person?.salespersonaction) !==
        normalizeStatus(prev?.salespersonaction) ||
      Boolean(person?.isProjectoutstanding) !==
        Boolean(prev?.isProjectoutstanding) ||
      Boolean(isOutstanding(person?.isProjectoutstanding)) !==
        Boolean(isOutstanding(prev?.isProjectoutstanding))
    );
  });
};

async function persistProjectAssignees(
  dispatch,
  project,
  updatedAssignedPerson,
  salesPersonStatus
) {
  const managerEmailRaw = project?.AssignedManagerEmail;
  const managerEmail = resolveManagerEmail(project);
  if (!managerEmail) {
    throw new Error(
      `Missing AssignedManagerEmail for project ${project?.ProjectName || project?._id}`
    );
  }
  if (!project?.quotationId) {
    throw new Error(
      `Missing quotationId for project ${project?.ProjectName || project?._id}`
    );
  }

  const updatedRow = {
    ProjectName: project.ProjectName,
    quotationId: project.quotationId,
    ProjectDueDate: project.ProjectDueDate || "",
    updateData: {
      // Preserve array vs string shape expected by backend
      AssignedManagerEmail: Array.isArray(managerEmailRaw)
        ? managerEmailRaw
        : managerEmail,
      Assignedperson: updatedAssignedPerson,
      SalesPersonStatus: salesPersonStatus,
      ProjectManagerStatus: salesPersonStatus,
    },
  };

  const formData = new FormData();
  formData.append("project", JSON.stringify(updatedRow));
  await dispatch(updateProjectByName(formData)).unwrap();
}

async function resolveProjectsForSync(quotationId, projects, leadMeta) {
  try {
    const response = await axiosInstance.get("/managerProjectRequested", {
      params: { _: Date.now() },
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    const allProjects = Array.isArray(response?.data) ? response.data : [];
    const freshMatches = collectLeadProjects(allProjects, quotationId, leadMeta);
    if (freshMatches.length) return freshMatches;
  } catch (fetchErr) {
    console.warn("syncLeadProjectBucket: failed to fetch all projects", fetchErr);
  }

  return collectLeadProjects(projects, quotationId, leadMeta);
}

/**
 * Keep salesperson project buckets in sync with lead Status:
 * - Won → Pending: assignees → response received (Response Received bucket)
 * - Pending → Won: assignees → project approved (Project Approved bucket)
 */
export async function syncLeadProjectBucketOnStatusChange(
  dispatch,
  { quotationId, prevStatus, nextStatus, projects, leadMeta = {} }
) {
  if (!quotationId) return { synced: 0, method: "none" };

  const wasWon = isWonLeadStatus(prevStatus);
  const isNowWon = isWonLeadStatus(nextStatus);
  if (wasWon === isNowWon) return { synced: 0, method: "none" };

  // Do NOT call /sales-status/update-lead-status with bucket names —
  // that API sets Leads.Status (Won/Pending/...), not SalesPersonStatus.
  // Lead Status is already updated via updateLead; only sync assignees here.
  const targetAssigneeStatus = isNowWon
    ? "project approved"
    : "response received";

  const leadProjects = await resolveProjectsForSync(
    quotationId,
    projects,
    leadMeta
  );

  // Response Received / Project Approved buckets use quoted (non-tracking) rows.
  const projectsToSync = leadProjects.filter(
    (proj) => proj?.isProjecttracking === false
  );

  if (!projectsToSync.length) {
    console.warn(
      "syncLeadProjectBucket: no manager projects found for quotationId",
      quotationId
    );
    return { synced: 0, method: "none", projectCount: 0 };
  }

  let synced = 0;
  const failures = [];

  for (const project of projectsToSync) {
    const assigned = Array.isArray(project?.Assignedperson)
      ? project.Assignedperson
      : [];
    if (!assigned.length) continue;

    if (!isNowWon && assigned.every(isForwardedAssignee)) {
      continue;
    }

    const updatedAssignedPerson = assigned.map((person) =>
      mapAssigneeForSync(person, isNowWon)
    );

    if (!assigneesChanged(assigned, updatedAssignedPerson)) continue;

    try {
      await persistProjectAssignees(
        dispatch,
        project,
        updatedAssignedPerson,
        targetAssigneeStatus
      );
      synced += 1;
    } catch (projectErr) {
      console.warn(
        "syncLeadProjectBucket: batch update failed, trying per-assignee",
        projectErr
      );

      try {
        let updatedAny = false;
        for (let index = 0; index < assigned.length; index += 1) {
          const nextPerson = mapAssigneeForSync(assigned[index], isNowWon);
          if (nextPerson === assigned[index]) continue;

          const partialAssigned = assigned.map((person, idx) =>
            idx === index ? nextPerson : person
          );

          await persistProjectAssignees(
            dispatch,
            project,
            partialAssigned,
            targetAssigneeStatus
          );
          updatedAny = true;
        }
        if (updatedAny) synced += 1;
      } catch (fallbackErr) {
        failures.push({
          projectId: project?._id,
          projectName: project?.ProjectName,
          error: fallbackErr,
        });
      }
    }
  }

  if (synced === 0 && failures.length > 0) {
    throw new Error(
      `Failed to sync project bucket for ${failures.length} project(s).`
    );
  }

  return {
    synced,
    method: "projects",
    projectCount: projectsToSync.length,
  };
}
